import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { FileText } from "lucide-react";
import { centsToUsd } from "@/lib/personnel/compensation";

const IRS_1099_THRESHOLD_CENTS = 60000; // $600

export default function TenNinetyNineSection({ instructor }: { instructor: any }) {
  const [year] = useState(new Date().getFullYear());
  const [record, setRecord] = useState<any>(null);
  const [compensationCents, setCompensationCents] = useState(0);
  const [saving, setSaving] = useState(false);
  const [sendMethod, setSendMethod] = useState<string>("");
  const [tracking, setTracking] = useState<string>("");
  const [status, setStatus] = useState<string>("");

  useEffect(() => {
    (async () => {
      const [rec, pay] = await Promise.all([
        (supabase.from as any)("instructor_tax_records")
          .select("*")
          .eq("instructor_id", instructor.id)
          .eq("tax_year", year)
          .maybeSingle(),
        (supabase.from as any)("payroll_ledger")
          .select("final_cents, paid_at")
          .eq("instructor_id", instructor.id)
          .is("voided_at", null),
      ]);
      const r = rec.data ?? null;
      setRecord(r);
      setSendMethod(r?.send_method ?? "");
      setTracking(r?.send_tracking_notes ?? "");
      setStatus(r?.draft_status ?? "");
      const total = ((pay.data ?? []) as any[])
        .filter((x) => new Date(x.paid_at).getFullYear() === year)
        .reduce((s, x) => s + (x.final_cents ?? 0), 0);
      setCompensationCents(total);
    })();
  }, [instructor.id, year]);

  const required = compensationCents >= IRS_1099_THRESHOLD_CENTS;
  const displayStatus = (() => {
    if (record?.date_1099_issued) return "Sent";
    if (record?.approved_at) return "Completed";
    if (record?.draft_status === "draft") return "Preparing";
    return "Not Started";
  })();

  const saveSend = async () => {
    if (!record?.id) {
      toast({ title: "No tax record yet", description: `Generate a ${year} draft from the Tax Center first.` });
      return;
    }
    setSaving(true);
    const { error } = await (supabase.from as any)("instructor_tax_records")
      .update({
        send_method: sendMethod || null,
        send_tracking_notes: tracking || null,
      })
      .eq("id", record.id);
    setSaving(false);
    if (error) { toast({ title: "Save failed", description: error.message, variant: "destructive" }); return; }
    toast({ title: "1099 send details saved" });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> 1099 Tracking · {year}</CardTitle>
        <CardDescription>Compensation is derived from the payroll ledger. 1099 required at $600.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label className="text-xs">{year} Compensation</Label><div className="font-medium">{centsToUsd(compensationCents)}</div></div>
          <div>
            <Label className="text-xs">1099 Required</Label>
            <div><Badge variant={required ? "default" : "secondary"}>{required ? "YES" : "NO"}</Badge></div>
          </div>
          <div>
            <Label className="text-xs">1099 Status</Label>
            <div>{displayStatus}</div>
          </div>
          <div>
            <Label className="text-xs">Date Sent</Label>
            <div>{record?.date_1099_issued ? new Date(record.date_1099_issued).toLocaleDateString() : "—"}</div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end border-t pt-3">
          <div>
            <Label className="text-xs">Send Method</Label>
            <Select value={sendMethod} onValueChange={setSendMethod}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mail">Mail</SelectItem>
                <SelectItem value="electronic">Electronic</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Tracking Notes</Label>
            <Textarea rows={2} value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Carrier, tracking number, delivery date…" />
          </div>
        </div>
        <div className="flex justify-end">
          <Button size="sm" onClick={saveSend} disabled={saving || !record?.id}>Save 1099 details</Button>
        </div>
        {!record?.id && (
          <p className="text-xs text-muted-foreground">Draft, approval, and issuance are managed in the Tax Center. Send-method and tracking notes here become editable once a {year} record exists.</p>
        )}
      </CardContent>
    </Card>
  );
}
